import { Autoplay, Manipulation, Navigation, Pagination, Swiper, Thumbs } from 'swiper';

function slideClearEffects(swiper) {
    const slide = swiper.slides[swiper.activeIndex],
        elementsWithEffects = slide.querySelectorAll('[data-swiper-effect]');

    elementsWithEffects.forEach((el) => {
        el.classList.remove('effect--' + el.dataset.swiperEffect);
    });
}

function slideActivatedEffect(swiper) {
    const slide = swiper.slides[swiper.activeIndex],
        elementsWithEffects = slide.querySelectorAll('[data-swiper-effect]');

    elementsWithEffects.forEach((el) => {
        el.classList.add('effect--' + el.dataset.swiperEffect);
    });
}

export function initSwiper(swiperElements) {
    // loop over swiper instances and init them
    swiperElements.forEach((swiper) => {
        let swiperInstance = null,
            destroyOn = swiper.dataset.destroyOn,
            height = swiper.dataset.height || undefined,
            centeredSlides = swiper.dataset.centeredSlides === 'true',
            slidesPerView = swiper.dataset.slidesPerView || 'auto',
            autoplay = swiper.dataset.autoplay,
            cssMode = swiper.dataset.cssMode === 'true',
            lazy = swiper.dataset.lazy === 'true',
            loop = swiper.dataset.loop === 'true',
            parentNode = swiper.parentNode,
            prevEl = parentNode.querySelector(
                '.swiper-button-prev:not(.swiper-container .swiper-container .swiper-button-prev)',
            ),
            nextEl = parentNode.querySelector(
                '.swiper-button-next:not(.swiper-container .swiper-container .swiper-button-next)',
            ),
            paginationEl = parentNode.querySelector('.swiper-pagination'),
            eventuallyMapAndDelegateEvent = (name, origEvent) => {
                // when clicking card-components, events must be mapped and delegated,
                // as swiper blocks all interaction with non-link elements
                const target = origEvent.target,
                    clickableCard = target?.closest('.card[data-clickable]');

                if (clickableCard) {
                    const mappedEvent = new Event(name, { bubbles: true });

                    mappedEvent.clientX = origEvent.clientX;
                    mappedEvent.clientY = origEvent.clientY;
                    mappedEvent.button = origEvent.button;

                    target.dispatchEvent(mappedEvent);
                }
            },
            createSwiper = () => {
                const nested = Boolean(parentNode.closest('.swiper-container[data-enabled="true"]')),
                    hasSubSwipers = Boolean(swiper.querySelector('.swiper-container[data-enabled="true"]')),
                    upcomingSlides = new Set(),
                    expiringSlides = new Set();

                if (loop && hasSubSwipers) {
                    loop = false;

                    if (process.env.NODE_ENV !== 'production') {
                        console.warn(
                            '[Swiper] A Swiper with nested Swipers may not loop as it breaks the nested Swipers',
                            swiper,
                        );
                    }
                }

                if (autoplay && isNaN(autoplay) === false) {
                    autoplay = {
                        delay: Number(autoplay),
                    };
                } else {
                    autoplay = autoplay === 'true' ? { delay: 3000 } : false;
                }

                slidesPerView = !isNaN(slidesPerView) ? Number(slidesPerView) : slidesPerView;

                // check if there are slides either about to be displayed or expiring (in case they got to the client due to caching)
                swiper.querySelectorAll('.swiper-slide').forEach((slide) => {
                    if (slide.parentNode.closest('.swiper-slide')) {
                        // ignore sub-swipers
                        return;
                    }

                    let showSlide = false;

                    if ('activeFrom' in slide.dataset) {
                        if (new Date(slide.dataset.activeFrom) < Date.now()) {
                            showSlide = true;
                        } else {
                            upcomingSlides.add(slide);
                            slide.remove();
                        }
                    } else {
                        showSlide = true;
                    }

                    if ('activeUntil' in slide.dataset) {
                        if (new Date(slide.dataset.activeUntil) < Date.now()) {
                            slide.remove();
                        } else {
                            expiringSlides.add(slide);
                            showSlide = true;
                        }
                    } else {
                        showSlide = true;
                    }

                    slide.hidden = !showSlide;
                });

                swiperInstance = new Swiper(swiper, {
                    // TODO check if new a11y module can be used instead of own implementation
                    modules: [Navigation, Pagination, Autoplay, Manipulation, Thumbs],
                    init: false,
                    loop,
                    autoHeight: height === 'auto',
                    height: !isNaN(height) ? Number(height) : undefined,
                    centeredSlides,
                    slidesPerView,
                    touchStartPreventDefault: true,
                    nested,
                    // prettier-ignore
                    ...(lazy
                        ? {
                            watchSlidesProgress: true,
                            watchSlidesVisibility: true,
                            preloadImages: false,
                            lazy: {
                                checkInView: true,
                                loadOnTransitionStart: true,
                                loadPrevNext: true,
                            },
                        }
                        : {}),
                    autoplay,
                    cssMode,
                    spaceBetween: Number(swiper.dataset.spaceBetween || 0),

                    navigation: {
                        nextEl,
                        prevEl,
                    },

                    pagination: {
                        el: paginationEl,
                        clickable: true,
                        type: 'custom',
                        renderCustom(swiper, current, total) {
                            if (total <= 1) {
                                return '';
                            }

                            let className = 'swiper-pagination-bullet',
                                html = '';

                            for (let i = 1; i <= total; i++) {
                                if (i === current) {
                                    html += `<button type="button" class="${className} ${className}-active" title="${CONFIG.i10n.swiper.currentSlide.replace(
                                        '%s',
                                        i,
                                    )}" disabled></button>`;
                                } else {
                                    html += `<button type="button" class="${className}" title="${CONFIG.i10n.swiper.goToSlide.replace(
                                        '%s',
                                        i,
                                    )}"></button>`;
                                }
                            }

                            return html;
                        },
                    },

                    a11y: {
                        enabled: false, // implementation is not ideal, do it manually
                    },

                    on: {
                        init() {
                            if (swiperInstance.params.loop) {
                                swiperInstance.slides.forEach((slide) => {
                                    if (slide.classList.contains(swiperInstance.params.slideDuplicateClass)) {
                                        slide.setAttribute('aria-hidden', 'true');
                                    }
                                });
                            }

                            if (prevEl) {
                                prevEl.disabled = swiperInstance.isBeginning;
                            }

                            if (nextEl) {
                                nextEl.disabled = swiperInstance.isEnd;
                            }

                            slideActivatedEffect(swiperInstance);
                        },
                        reachBeginning() {
                            if (prevEl && swiperInstance.params.loop !== true) {
                                prevEl.disabled = true;
                            }
                        },
                        reachEnd() {
                            if (nextEl && swiperInstance.params.loop !== true) {
                                nextEl.disabled = true;
                            }
                        },
                        fromEdge() {
                            if (prevEl) {
                                prevEl.disabled = false;
                            }

                            if (nextEl) {
                                nextEl.disabled = false;
                            }
                        },
                        slideChangeTransitionStart() {
                            slideClearEffects(swiperInstance);
                        },
                        slideChangeTransitionEnd() {
                            slideActivatedEffect(swiperInstance);
                        },
                        touchStart(swiper, event) {
                            eventuallyMapAndDelegateEvent('mousedown', event);
                        },
                        touchEnd(swiper, event) {
                            eventuallyMapAndDelegateEvent('mouseup', event);
                        },
                    },
                });

                swiperInstance.init();

                // check regularly, whether upcoming slides should be shown
                if (upcomingSlides.size) {
                    let slideChecker;

                    slideChecker = setInterval(() => {
                        requestAnimationFrame(() => {
                            const localSlides = new Set(upcomingSlides);

                            for (const slide of localSlides) {
                                if (new Date(slide.dataset.activeFrom) < Date.now()) {
                                    swiperInstance.appendSlide(slide);

                                    upcomingSlides.delete(slide);
                                }
                            }

                            if (upcomingSlides.size === 0) {
                                clearInterval(slideChecker);
                            }
                        });
                    }, 1000);
                }

                // check regularly, whether currently present slides are expiring and remove them in case
                if (expiringSlides.size) {
                    let slideChecker;

                    slideChecker = setInterval(() => {
                        requestAnimationFrame(() => {
                            const localSlides = new Set(expiringSlides);

                            for (const slide of localSlides) {
                                if (new Date(slide.dataset.activeUntil) < Date.now()) {
                                    slide.remove();

                                    if (loop) {
                                        // also remove all duplicates of the expiring slides
                                        // (swiper create them when looping)
                                        swiperInstance.slides.forEach((slide_) => {
                                            if (
                                                'activeUntil' in slide_.dataset &&
                                                slide_.dataset.activeUntil === slide.dataset.activeUntil
                                            ) {
                                                slide_.remove();
                                            }
                                        });
                                    }

                                    swiperInstance.update();
                                    expiringSlides.delete(slide);
                                }
                            }

                            if (expiringSlides.size === 0) {
                                clearInterval(slideChecker);
                            }
                        });
                    }, 1000);
                }
            },
            resizeCallback = () => {
                // destroy swiper after a given breakpoint
                if (Foundation.MediaQuery.atLeast(destroyOn)) {
                    swiper.parentElement.classList.add('swiper--destroyed');

                    if (swiperInstance !== null) {
                        swiperInstance.destroy(true, true);
                        swiperInstance = null;
                    }
                } else if (swiperInstance === null) {
                    swiper.parentElement.classList.remove('swiper--destroyed');

                    createSwiper();
                }
            };

        if (destroyOn) {
            $(window).on('changed.zf.mediaquery', resizeCallback); // it is not possible to listen for jQuery events in vanilla JS

            resizeCallback();
        } else {
            createSwiper();
        }
    });
}
